import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { DispatchOrderDto, DispatchPickListDto, DispatchPickListLineDto, GenerateDispatchPickListRequest } from '@/types/dispatch';

export default function DispatchPickListPage() {
  const { t } = useTranslation('dispatch');
  const queryClient = useQueryClient();

  const { data: pickLists = [], isLoading } = useQuery({
    queryKey: ['dispatch-pick-lists'],
    queryFn: async () => (await apiClient.get<DispatchPickListDto[]>('/dispatch-pick-lists')).data,
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['dispatch-orders'],
    queryFn: async () => (await apiClient.get<DispatchOrderDto[]>('/dispatch-orders')).data,
  });
  const { data: processConfiguration } = useProcessConfiguration();
  // When generation is "Auto", DispatchOrderService.ReleaseAsync already generates the pick list
  // as part of Release — manual generation from here would be redundant (and there'd never be a
  // still-ungenerated released order to pick from anyway), so the button hides entirely. Stays
  // visible until the config has loaded, matching AppShell's nav-gating convention, and defaults to
  // visible (Manual) if it somehow never loads rather than silently hiding the only way to generate one.
  const manualGenerationAllowed = !processConfiguration || processConfiguration.dispatchPickListGenerationMode !== 'Auto';

  // Only a released, still-open order with no pick list yet can have one generated.
  const pickListedOrderIds = new Set(pickLists.map((p) => p.dispatchOrderId));
  const generatableOrders = orders.filter((o) => o.status === 'Released' && !o.isClosed && !pickListedOrderIds.has(o.id));

  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [viewing, setViewing] = useState<DispatchPickListDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['dispatch-pick-lists'] });

  const generateMutation = useMutation({
    mutationFn: () => {
      const payload: GenerateDispatchPickListRequest = { dispatchOrderId: orderId };
      return apiClient.post('/dispatch-pick-lists/generate', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
      setOrderId('');
    },
  });

  const columns: DataTableColumn<DispatchPickListDto>[] = [
    { field: 'pickListNumber', headerName: t('pickList.table.number') },
    { field: 'orderNumber', headerName: t('table.dispatchOrder'), filterable: true },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Button size="small" onClick={() => setViewing(r)}>{t('view')}</Button>
      ),
    },
  ];

  const lineColumns: DataTableColumn<DispatchPickListLineDto>[] = [
    { field: 'materialCode', headerName: t('table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'barcode', headerName: t('pickList.barcode'), render: (l) => l.barcode ?? '—' },
    { field: 'locationCode', headerName: t('pickList.location'), render: (l) => l.locationCode ?? '—' },
    { field: 'batchNo', headerName: t('pickList.batch'), render: (l) => l.batchNo ?? '—' },
    { field: 'requiredQty', headerName: t('pickList.requiredQty') },
    { field: 'pickedQty', headerName: t('table.pickedQty') },
    { field: 'status', headerName: t('table.status'), render: (l) => <StatusChip status={l.status} /> },
  ];

  return (
    <>
      <PageHeader
        title={t('pickList.title')}
        subtitle={t('pickList.subtitle')}
        actions={
          manualGenerationAllowed ? (
            <IfPermitted permission={PERMISSIONS.PICKING_EXECUTE}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                {t('pickList.generate')}
              </Button>
            </IfPermitted>
          ) : undefined
        }
      />

      <DataTable columns={columns} rows={pickLists} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="dispatch-pick-lists" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('pickList.generate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('pickList.selectOrder')} value={orderId} onChange={setOrderId} fullWidth helperText={t('pickList.selectOrderHint')}
              options={generatableOrders.map((o) => ({
                value: o.id,
                label: `${o.orderNumber} — ${o.destinationType === 'Warehouse' ? `→ ${o.destinationWarehouseName}` : o.customerName}`,
              }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={!orderId || generateMutation.isPending} onClick={() => generateMutation.mutate()}>
            {t('pickList.generate')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.pickListNumber ?? ''}
        subtitle={viewing ? `${viewing.orderNumber} · ${viewing.warehouseName}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
